import CircularProgress, {
  CircularProgressProps,
} from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { EmtTypography } from "./EmtTypography";

export type EmtCircularProgressProps = Omit<CircularProgressProps, "color"> & {
  /**
   * Show / hide right text of progress circle
   * @default true
   */
  activeStep?: boolean;
  /**
   * Title of "stepping"
   * @default Step
   */
  stepTitle?: string | null;
  /**
   * The current step
   * @default 1
   */
  currentStep?: number;
  /**
   * Total step
   * @default 3
   */
  totalStep?: number;
  /**
   * The current Step title
   * @default undefined
   */
  currentStepTitle?: string | null;
  /**
   * Color for the progress indicator
   * @default theme.palette.success.dark
   */
  progressColor?: string;
  steps?: boolean;
  nonce?: string;
  color?: any;
};

function CircularProgressWithLabel({
  progressColor,
  ...props
}: CircularProgressProps & { value: number; progressColor?: string }) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress
        variant="determinate"
        sx={{
          color: (theme: any) => (theme as any).palette.greyGradient[100],
        }}
        size={props.size}
        thickness={props.thickness}
        value={100}
      />
      <CircularProgress
        {...props}
        sx={{
          ...props.sx,
          color: progressColor
            ? progressColor
            : (theme: any) => theme.palette.success.dark,
          position: "absolute",
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <EmtTypography
          variant="body2"
          fontWeight="bold"
          color={props.color}
        >{`${Math.round(props.value)}%`}</EmtTypography>
      </Box>
    </Box>
  );
}

export function EmtCircularProgress(props: EmtCircularProgressProps) {
  const {
    color,
    activeStep = true,
    stepTitle = "Step",
    currentStep = 1,
    totalStep = 3,
    currentStepTitle,
    steps = true,
    progressColor,
    ...rest
  } = props;

  const defaultThickness = 4;
  const defaultVariant = "determinate";
  const defaultSize = 52;
  const defaultValue = 0;

  const circularProgressProps = {
    ...rest,
    thickness: props.thickness ? props.thickness : defaultThickness,
    variant: props.variant ? props.variant : defaultVariant,
    size: props.size ? props.size : defaultSize,
    value: props.value ? (props.value > 100 ? 100 : props.value) : defaultValue,
    color: color,
    progressColor: progressColor, // Pass the progressColor prop
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {activeStep && steps && (
        <Box sx={{ textAlign: "right", marginRight: "14px" }}>
          <EmtTypography variant="caption" color={color}>
            {stepTitle} {currentStep}/{totalStep}
          </EmtTypography>
          {currentStepTitle && (
            <EmtTypography variant="body2" color={color} fontWeight={600}>
              {currentStepTitle}
            </EmtTypography>
          )}
        </Box>
      )}
      <CircularProgressWithLabel {...circularProgressProps} />
    </Box>
  );
}
